import registerTemplate from "../templates/pages/register.html?raw";
import { FIELDS } from "../constants/register";
import { cognitoSignUp, cognitoConfirmSignUp, cognitoResendConfirmationCode } from "../services/cognito";
import { saveJsonToLocalStorage } from "../services/localStorage";
import { getElementById } from "../services/dom";
import logoUrl from "../assets/logo.png";

export default class RegisterPage {
  private currentTab = 0;
  private registerForm: HTMLFormElement;
  private steps: HTMLCollectionOf<Element>;
  private fields: { [key: string]: string };
  constructor() {
    this.steps = document.getElementsByClassName("step");
    this.loadPage();
    this.regeneratePrevNextButtons();
    this.registerForm = document.getElementById("signUpForm") as HTMLFormElement;
    this.fields = {};

    this.registerForm.addEventListener("submit", (event) => {
      console.log("submitting form");
      event.preventDefault();
      this.submitForm(event);
    });
  }

  private loadPage(): void {
    const style = document.createElement("link");
    style.rel = "stylesheet";
    style.href = "../styles/register.css";
    document.head.appendChild(style);
    document.querySelector<HTMLDivElement>("#app")!.innerHTML = registerTemplate;
    this.showTab(this.currentTab);

    // Set up Logo
    const logoElement = getElementById("section-logo");
    const logoImgMobile = document.createElement("img");
    logoImgMobile.src = logoUrl;
    logoImgMobile.alt = "Logo";
    logoImgMobile.classList.add("login-image");
    logoElement.appendChild(logoImgMobile);
  }

  private showTab(tabNumber: number) {
    // This function will display the specified tab of the form...
    (this.steps[tabNumber] as HTMLElement).style.display = "block";
    //... and fix the Previous/Next buttons:
    this.regeneratePrevNextButtons();
    //... and run a function that will display the correct step indicator:
    this.updateStepIndicator(tabNumber);
    this.populateForm();
  }

  private nextPrev(move: number) {
    // This function will figure out which tab to display
    // Exit the function if any field in the current tab is invalid:
    if (move == 1 && !this.validateForm()) {
      console.log("form not valid or trying to move more than 1 step!");
      return false;
    }
    // Store current tab data
    this.storeTabFormData();
    // Hide the current tab:
    (this.steps[this.currentTab] as HTMLElement).style.display = "none";
    // Increase or decrease the current tab by 1:
    this.currentTab = this.currentTab + move;
    // Otherwise, display the correct tab:
    this.showTab(this.currentTab);
  }

  private inputError(input: HTMLInputElement, errorMessage: string) {
    input.className += " invalid";
    const errorDiv = document.createElement("div");
    errorDiv.className = "text-red-500 text-xs italic";
    errorDiv.innerHTML = errorMessage;
    input.parentNode!.append(errorDiv);
  }

  private validateForm() {
    // This function deals with validation of the form fields
    let valid = true;
    const currentStepInputs = this.steps[this.currentTab].getElementsByTagName("input");
    
    // Clear previous validation errors
    const errorMessages = this.steps[this.currentTab].querySelectorAll(".text-red-500");
    errorMessages.forEach(msg => msg.remove());
    
    // Remove invalid class from all inputs
    for (let input = 0; input < currentStepInputs.length; input++) {
      currentStepInputs[input].className = currentStepInputs[input].className.replace(" invalid", "");
    }
    
    // A loop that checks every input field in the current tab:
    for (let input = 0; input < currentStepInputs.length; input++) {
      // If a field is empty...
      if (currentStepInputs[input].value == "") {
        console.log(`input ${currentStepInputs[input].name} is empty`);
        // add an "invalid" class to the field:
        this.inputError(currentStepInputs[input], "This field is required");
        // and set the current valid status to false
        valid = false;
      }

      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const passwordRegex = /^[\S]+.*[\S]+$/; // Must start and end with non-whitespace

      if (currentStepInputs[input].name === "email" && !emailRegex.test(currentStepInputs[input].value)) {
        this.inputError(currentStepInputs[input], "Invalid email address");
        valid = false;
      }
      
      // Password validation - must start and end with non-whitespace, be at least 10 chars
      if (currentStepInputs[input].name === "password") {
        const password = currentStepInputs[input].value;
        if (password.length < 10) {
          this.inputError(currentStepInputs[input], "Password must be at least 10 characters");
          valid = false;
        } else if (!passwordRegex.test(password)) {
          this.inputError(currentStepInputs[input], "Password cannot start or end with spaces");
          valid = false;
        } else if (!/(?=.*[a-z])/.test(password)) {
          this.inputError(currentStepInputs[input], "Password must contain at least 1 lowercase letter");
          valid = false;
        } else if (!/(?=.*[A-Z])/.test(password)) {
          this.inputError(currentStepInputs[input], "Password must contain at least 1 uppercase letter");
          valid = false;
        } else if (!/(?=.*[0-9])/.test(password)) {
          this.inputError(currentStepInputs[input], "Password must contain at least 1 number");
          valid = false;
        }
      }
      
      // Password confirmation validation
      if (currentStepInputs[input].name === "confirmPassword") {
        const passwordInput = document.querySelector('input[name="password"]') as HTMLInputElement;
        if (passwordInput && currentStepInputs[input].value !== passwordInput.value) {
          this.inputError(currentStepInputs[input], "Passwords do not match");
          valid = false;
        }
      }
      
      // Name validation - ensure they contain valid characters
      if (currentStepInputs[input].name === "firstName" || currentStepInputs[input].name === "lastName") {
        const nameValue = currentStepInputs[input].value.trim();
        if (nameValue.length < 1) {
          this.inputError(currentStepInputs[input], "This field is required");
          valid = false;
        } else if (!/^[\p{L}\p{M}\p{S}\p{N}\p{P}\s]+$/u.test(nameValue)) {
          this.inputError(currentStepInputs[input], "Please enter a valid name");
          valid = false;
        }
      }
    }
    // If the valid status is true, mark the step as finished and valid:
    if (valid) {
      document.getElementsByClassName("stepIndicator")[this.currentTab].className += " finish";
    }
    return valid; // return the valid status
  }

  private updateStepIndicator(tabNumber: number) {
    // This function removes the "active" class of all steps...
    var i,
      x = document.getElementsByClassName("stepIndicator");
    for (i = 0; i < x.length; i++) {
      x[i].className = x[i].className.replace(" active", "");
    }
    //... and adds the "active" class on the current step:
    x[tabNumber].className += " active";
  }

  private populateForm() {
    this.fields = JSON.parse(localStorage.getItem("registerFormData") || "{}");
    const formElements = document.querySelectorAll(".registerInput");
    formElements.forEach((element) => {
      const inputField = element.getAttribute("data-inputField")!;
      const { id, type, placeholder, name, autocomplete } = FIELDS[inputField];
      const innerElement = document.createElement("input");
      innerElement.id = `register-inputField-${id}`;
      innerElement.type = type;
      innerElement.placeholder = placeholder;
      innerElement.name = name;
      if (autocomplete) {
        innerElement.autocomplete = autocomplete;
      }
      innerElement.classList.value = "w-full px-4 py-3 rounded-md text-gray-700 font-medium border-solid border-2 border-gray-200";
      
      // Set the value from stored data if available
      if (this.fields[name]) {
        innerElement.value = this.fields[name];
      }
      
      element.innerHTML = innerElement.outerHTML;
    });
  }

  private storeTabFormData() {
    const currentStepInputs = this.steps[this.currentTab].getElementsByTagName("input");
    for (let input = 0; input < currentStepInputs.length; input++) {
      this.fields[currentStepInputs[input].name] = currentStepInputs[input].value;
      saveJsonToLocalStorage("registerFormData", this.fields);
    }
  }

  private async submitForm(event: SubmitEvent) {
    event.preventDefault();
    
    // Validate final form
    if (!this.validateForm()) {
      this.showFormStatus("Please fix the errors above", "error");
      return;
    }

    // Show loading state
    this.showFormStatus("Creating your account...", "loading");
    
    const submitBtn = document.getElementById("submitBtn") as HTMLButtonElement;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = "Creating Account...";
    }
    
    // Store current step data before submission
    this.storeTabFormData();
    
    // Get form data from localStorage (where it's stored during wizard steps)
    const storedData = JSON.parse(localStorage.getItem("registerFormData") || "{}");
    
    const email = storedData.email?.trim() || "";
    const password = storedData.password?.trim() || "";
    const firstName = storedData.firstName?.trim() || "";
    const lastName = storedData.lastName?.trim() || "";
    
    console.log("Form data being sent:", { email, password: password ? "[REDACTED]" : "", firstName, lastName });
    
    // Validate required fields
    if (!email || !password || !firstName || !lastName) {
      this.showFormStatus("All fields are required", "error");
      const submitBtn = document.getElementById("submitBtn") as HTMLButtonElement;
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = "Submit";
      }
      return;
    }
    
    try {
      await cognitoSignUp(email, password, firstName, lastName);
      
      // Show email confirmation step
      this.showEmailConfirmation(email);
      
    } catch (error: any) {
      console.error("Registration error:", error);
      this.showFormStatus(`Registration failed: ${error.message}`, "error");
      
      const submitBtn = document.getElementById("submitBtn") as HTMLButtonElement;
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = "Submit";
      }
    }
  }

  private showEmailConfirmation(email: string) {
    const confirmationHtml = `
      <div class="container mx-auto w-96">
        <div id="section-logo"></div>
        <div class="px-12 rounded-2xl mx-auto fade-in-image">
          <h1 class="text-lg font-bold leading-tight text-center pb-2">Confirm Your Email</h1>
          <p class="text-center text-sm mb-4">Please enter the verification code sent to your email address.</p>
          
          <form id="confirmForm">
            <div class="mb-4">
              <label class="block text-sm font-medium mb-2">Email:</label>
              <input type="email" id="confirmEmail" value="${email}" readonly class="w-full px-4 py-3 rounded-md text-gray-700 font-medium border-solid border-2 border-gray-200 bg-gray-100" />
            </div>
            
            <div class="mb-4">
              <label class="block text-sm font-medium mb-2">Verification Code:</label>
              <input type="text" id="confirmationCode" required placeholder="Enter 6-digit code" class="w-full px-4 py-3 rounded-md text-gray-700 font-medium border-solid border-2 border-gray-200" />
            </div>
            
            <button type="submit" id="confirmBtn" class="w-full border border-transparent focus:outline-none p-3 rounded-md text-center text-white bg-indigo-600 hover:bg-indigo-700 text-lg mb-4">Verify Email</button>
          </form>
          
          <div class="text-center space-y-2">
            <button id="resendCodeBtn" class="text-indigo-600 hover:text-indigo-800 text-sm block mx-auto">Resend code</button>
            <button id="alreadyVerifiedBtn" class="text-gray-600 hover:text-gray-800 text-sm block mx-auto">Already verified? Go to login</button>
          </div>
          
          <div id="confirmationResponse" class="mt-4 p-3 rounded-md hidden"></div>
        </div>
      </div>
    `;
    
    document.querySelector<HTMLDivElement>("#app")!.innerHTML = confirmationHtml;
    
    // Set up logo
    const logoElement = getElementById("section-logo");
    const logoImgMobile = document.createElement("img");
    logoImgMobile.src = logoUrl;
    logoImgMobile.alt = "Logo";
    logoImgMobile.classList.add("login-image");
    logoElement.appendChild(logoImgMobile);
    
    // Set up event listeners
    this.setupConfirmationListeners(email);
  }
  
  private setupConfirmationListeners(email: string) {
    const confirmForm = document.getElementById("confirmForm") as HTMLFormElement;
    const confirmBtn = document.getElementById("confirmBtn") as HTMLButtonElement;
    const resendCodeBtn = document.getElementById("resendCodeBtn") as HTMLButtonElement;
    const alreadyVerifiedBtn = document.getElementById("alreadyVerifiedBtn") as HTMLButtonElement;
    const confirmationCodeInput = document.getElementById("confirmationCode") as HTMLInputElement;
    
    confirmForm.addEventListener("submit", async (e) => {
      e.preventDefault();
      
      const confirmationCode = confirmationCodeInput.value.trim();
      
      if (!confirmationCode) {
        this.showConfirmationResponse("Please enter the confirmation code", "error");
        return;
      }
      
      confirmBtn.disabled = true;
      confirmBtn.textContent = "Verifying...";
      this.showConfirmationResponse("Verifying your email...", "loading");
      
      try {
        await cognitoConfirmSignUp(email, confirmationCode);
        this.showConfirmationResponse("Email verified successfully! You can now login.", "success");
        
        // Clear form data from localStorage
        localStorage.removeItem("registerFormData");
        
        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
        
      } catch (error: any) {
        this.showConfirmationResponse(`Verification failed: ${error.message}`, "error");
        confirmBtn.disabled = false;
        confirmBtn.textContent = "Verify Email";
      }
    });
    
    resendCodeBtn.addEventListener("click", async (e) => {
      e.preventDefault();
      
      try {
        await cognitoResendConfirmationCode(email);
        this.showConfirmationResponse("Verification code resent successfully!", "success");
      } catch (error: any) {
        this.showConfirmationResponse(`Failed to resend code: ${error.message}`, "error");
      }
    });
    
    alreadyVerifiedBtn.addEventListener("click", (e) => {
      e.preventDefault();
      
      // Clear form data from localStorage
      localStorage.removeItem("registerFormData");
      
      // Redirect to login page
      window.location.href = "/login";
    });
  }
  
  private showConfirmationResponse(message: string, type: 'success' | 'error' | 'loading') {
    const responseDiv = document.getElementById("confirmationResponse") as HTMLDivElement;
    responseDiv.textContent = message;
    responseDiv.className = `mt-4 p-3 rounded-md ${type === 'success' ? 'bg-green-100 border border-green-400 text-green-700' : type === 'error' ? 'bg-red-100 border border-red-400 text-red-700' : 'bg-blue-100 border border-blue-400 text-blue-700'}`;
    responseDiv.classList.remove('hidden');
  }
  
  private showFormStatus(message: string, type: 'success' | 'error' | 'loading') {
    const statusDiv = document.getElementById("form-status") as HTMLDivElement;
    if (!statusDiv) return;
    
    statusDiv.textContent = message;
    statusDiv.className = `form-status mb-4 p-3 rounded-md ${
      type === 'success' ? 'bg-green-100 border border-green-400 text-green-700' : 
      type === 'error' ? 'bg-red-100 border border-red-400 text-red-700' : 
      'bg-blue-100 border border-blue-400 text-blue-700'
    }`;
    statusDiv.classList.remove('hidden');
    
    // Scroll to status message
    statusDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private regeneratePrevNextButtons() {
    const formFooter = document.getElementById("form-footer");
    formFooter!.innerHTML = "";
    const prevButton = document.createElement("button");
    prevButton.id = "prevBtn";
    prevButton.innerHTML = "Previous";
    prevButton.classList.value = "flex-1 focus:outline-none border border-gray-300 py-2 px-5 rounded-lg shadow-sm text-center text-gray-700 bg-white hover:bg-gray-100 text-lg fade-in-image";
    prevButton.style.display = "inline";
    const nextButton = document.createElement("button");
    nextButton.id = "nextBtn";
    nextButton.innerHTML = "Next";
    nextButton.classList.value = "flex-1 border border-transparent focus:outline-none p-3 rounded-md text-center text-white bg-indigo-600 hover:bg-indigo-700 text-lg fade-in-image";
    const submitButton = document.createElement("button");
    submitButton.type = "submit";
    submitButton.id = "submitBtn";
    submitButton.innerHTML = "Submit";
    submitButton.classList.value = "flex-1 border border-transparent focus:outline-none p-3 rounded-md text-center text-white bg-indigo-600 hover:bg-indigo-700 text-lg fade-in-image";
    submitButton.style.display = "inline";
    submitButton.onsubmit = (event) => {
      event.preventDefault();
    }
    submitButton.onclick = (event) => {
      event.preventDefault();
      this.submitForm(event as any);
    };
    if (this.currentTab != 0) {
      formFooter?.appendChild(prevButton);
    }
    if (this.currentTab == this.steps.length - 1) {
      formFooter?.appendChild(submitButton);
    } else {
      formFooter?.appendChild(nextButton);
    }

    nextButton.addEventListener("click", () => {
      this.nextPrev(1);
    });

    prevButton.addEventListener("click", () => {
      this.nextPrev(-1);
    });
  }
}
